<template>
  <div class="flex flex-col h-full md:max-h-[80vh] md:min-h-[60vh]">
    <template v-if="authMode">
      <UnifiedOrderAuth
        :tenant-id="props.tenantId!"
        :table-no="props.tableNo!"
        @back="exitAuthMode"
        @close="handleClose"
        @order-complete="handleAuthOrderComplete"
        @instant-reservation-done="handleInstantReservationDone"
        @table-unavailable="handleAuthTableUnavailable"
      />
    </template>

    <template v-else>
      <div
        class="flex items-center justify-between pb-2 border-b border-border -mt-2 -mx-2 px-2 shrink-0"
      >
        <div class="flex items-center gap-2">
          <h2 class="text-base font-semibold text-text-primary m-0">Cart</h2>
          <span
            v-if="cart.items.length > 0"
            class="flex items-center justify-center min-w-[1.5rem] h-6 w-6 rounded-full border border-border text-text-primary text-sm font-semibold"
          >
            {{ cart.items.length }}
          </span>
        </div>
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
          aria-label="Close cart"
          @click="handleClose"
        >
          <Icon name="x" :size="20" weight="bold" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-5 py-4">
        <CartItems :items="cart.items" @remove="handleRemove" @edit="handleEdit" />
      </div>

      <div class="pt-4 border-t border-border bg-white shrink-0">
        <div class="flex justify-end items-center mb-3 gap-x-2">
          <span class="text-text-secondary">Total Price</span>
          <span class="text-xl font-bold text-primary">{{ formatRs(cartTotal) }}</span>
        </div>

        <p v-if="props.tableBookedByOther" class="text-xs text-center text-error m-0 py-1">
          This table is booked by someone else. Please contact the waiter or restaurant.
        </p>

        <p
          v-else-if="!effectiveCanPlaceOrder && authStore.isAuthenticated"
          class="text-xs text-center text-text-secondary m-0 py-1"
        >
          Ordering unavailable — your session may have expired.
        </p>

        <Button
          v-else-if="
            !effectiveCanPlaceOrder &&
            isQrFlow &&
            props.tenantId &&
            props.tableNo &&
            !props.tableBookedByOther
          "
          variant="primary"
          size="large"
          full-width
          class="rounded-xl"
          :disabled="cart.items.length === 0"
          @click="enterAuthMode"
        >
          Place Order
        </Button>

        <div
          v-else-if="!effectiveCanPlaceOrder && !isQrFlow && !props.tableBookedByOther"
          class="mb-4"
        >
          <p class="text-sm text-text-secondary mb-3 text-center">
            Please sign in to place your order
          </p>
          <div class="flex gap-2">
            <Button variant="outline" full-width @click="$emit('login-click')">Sign In</Button>
            <Button variant="primary" full-width @click="$emit('signup-click')">Sign Up</Button>
          </div>
        </div>

        <template v-else>
          <Button
            variant="primary"
            size="large"
            full-width
            class="rounded-xl"
            :loading="placingOrderState || isValidatingReservation"
            :disabled="cart.items.length === 0 || !effectiveCanPlaceOrder || placingOrderState || isValidatingReservation"
            @click="handlePlaceOrder"
          >
            Order Now
          </Button>
          <p class="text-xs text-text-secondary m-0 mt-2">
            This order will go directly to kitchen.
          </p>
        </template>
      </div>
    </template>

    <MenuItemCustomizeDrawer
      v-model="editDrawerOpen"
      :item="editingMenuItem"
      :editing-cart-item="editingCartItem"
      :tenant-id="props.tenantId ?? ''"
      v-bind="
        props.activeReservationId != null ? { activeReservationId: props.activeReservationId } : {}
      "
      @update-cart-item="handleUpdateCartItem"
    />
    <ReservationProcessingOverlay
      :model-value="placingOrderState"
      message="Placing your order. Please wait..."
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Button from '@/components/common/Button.vue'
import Icon from '@/components/atoms/Icon.vue'
import CartItems from './CartItems.vue'
import UnifiedOrderAuth from './UnifiedOrderAuth.vue'
import MenuItemCustomizeDrawer from '@/components/features/restaurant/MenuItemCustomizeDrawer.vue'
import ReservationProcessingOverlay from '@/components/features/reservations/ReservationProcessingOverlay.vue'
import { useOrderStore } from '@/stores/order'
import { useAuthStore } from '@/stores/auth'
import { useOrder } from '@/composables/features/useOrder'
import { useCurrency } from '@/composables/core/useCurrency'
import { useReservationSessionGuard } from '@/composables/features/useReservationSessionGuard'
import { useNotifications } from '@/composables/core/useNotifications'
import { useRestaurantStore } from '@/stores/restaurant'
import { useAppFlow } from '@/composables/core/useAppFlow'
import type { Cart, OrderItem } from '@/interfaces'
import type { MenuItem } from '@/interfaces'

interface Props {
  cart: Cart
  canPlaceOrder: boolean
  placingOrder?: boolean
  tenantId?: string
  tableNo?: string
  activeReservationId?: string
  tableBookedByOther?: boolean
}

const props = defineProps<Props>()

const router = useRouter()
const { currentFlow } = useAppFlow()
const orderStore = useOrderStore()
const authStore = useAuthStore()
const { showError } = useNotifications()
const { validateReservationStillActive, isValidatingReservation } = useReservationSessionGuard()
const emit = defineEmits<{
  'place-order': []
  'login-click': []
  'signup-click': []
  'order-complete': [reservationId?: string]
  'guest-success': [reservationId: string]
  'table-unavailable': []
  'auth-mode-changed': [value: boolean]
  close: []
}>()
const restaurantStore = useRestaurantStore()
const { removeFromCart, updateCartItem } = useOrder()

const loading = ref<boolean>(false)
const editDrawerOpen = ref<boolean>(false)
const editingCartItem = ref<OrderItem | null>(null)
const editingMenuItem = ref<MenuItem | null>(null)
const authMode = ref<boolean>(false)
const reservationExpired = ref<boolean>(false)

const cartTotal = computed(() => orderStore.cartTotal)
const effectiveCanPlaceOrder = computed(() => props.canPlaceOrder && !reservationExpired.value)
const placingOrderState = computed(() => loading.value || !!props.placingOrder)

const isQrFlow = computed(() => !!(props.tableNo && props.tenantId))

watch(
  () => [props.activeReservationId, props.canPlaceOrder] as const,
  () => {
    reservationExpired.value = false
  }
)

function enterAuthMode(): void {
  authMode.value = true
  emit('auth-mode-changed', true)
}

function exitAuthMode(): void {
  authMode.value = false
  emit('auth-mode-changed', false)
}

const { formatDefaultPrice: formatRs } = useCurrency()

function handleClose(): void {
  authMode.value = false
  emit('auth-mode-changed', false)
  emit('close')
}

function handleRemove(itemId: string, attributeId: string): void {
  removeFromCart(itemId, attributeId)
}

function handleEdit(item: OrderItem): void {
  const menuItem = restaurantStore.getMenuItemById(item.item_id)
  if (menuItem) {
    editingMenuItem.value = menuItem
  } else {
    editingMenuItem.value = {
      id: item.item_id,
      item_id: item.item_id,
      title: item.item_name,
      price: item.item_amount ?? 0,
      item_attributes: [],
    }
  }
  editingCartItem.value = item
  editDrawerOpen.value = true
}

function handleUpdateCartItem(newItem: OrderItem): void {
  const previous = editingCartItem.value
  if (previous) {
    updateCartItem(previous.item_id, previous.attribute_id, newItem)
  }
  editingCartItem.value = null
  editingMenuItem.value = null
  editDrawerOpen.value = false
}

const handleOrderComplete = (reservationId?: string): void => {
  emit('close')
  if (!isQrFlow.value) {
    router.push({
      path: '/my-orders',
      query: {
        flow: currentFlow.value,
        ...(reservationId ? { reservation_id: reservationId } : {}),
      },
    })
  }
  emit('order-complete', reservationId)
}

const handleAuthOrderComplete = (reservationId?: string): void => {
  exitAuthMode()
  handleOrderComplete(reservationId)
}

const handleInstantReservationDone = (reservationId: string): void => {
  exitAuthMode()
  emit('close')
  emit('guest-success', reservationId)
}

const handleAuthTableUnavailable = (): void => {
  exitAuthMode()
  emit('table-unavailable')
}

const handlePlaceOrder = async (): Promise<void> => {
  if (placingOrderState.value || isValidatingReservation.value) return
  try {
    if (authStore.isAuthenticated && props.activeReservationId) {
      const id = Number(props.activeReservationId)
      if (!Number.isNaN(id)) {
        const validation = await validateReservationStillActive(id)
        if (!validation.ok) {
          reservationExpired.value = true
          showError(validation.message)
          return
        }
      }
    }
    emit('place-order')
  } catch {
    // handled
  }
}

watch(
  () => props.placingOrder,
  value => {
    if (!value) {
      loading.value = false
    }
  }
)
</script>
